import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import Loadable from './loadable';
import RoleBasedGuard from '../guards/RoleBasedGuard';
import FeatureGuard from '../guards/FeatureGuard';
import { PATH_AFTER_LOGIN, ROLE } from '../config';
import { FEATURES } from '../constants/features';
import EmployeeDashboardLayout from '../layouts/employee/dashboard';
import EmployeePendingCourses from '../pages/employee/learning/EmployeePendingCourses';
import EmployeeCompletedCourses from '../pages/employee/learning/EmployeeCompletedCourses';

const AdminGoalsPage = Loadable(lazy(() => import('../pages/admin/goal/AdminGoalsPage')));
const AdminGoalPage = Loadable(lazy(() => import('../pages/admin/goal/AdminGoalPage')));
const AdminSurveysPage = Loadable(lazy(() => import('../pages/admin/survey/AdminSurveysPage')));
const AdminSurveyCreatePage = Loadable(
  lazy(() => import('../pages/admin/survey/AdminSurveyCreatePage'))
);
const AdminSurveyEditPage = Loadable(
  lazy(() => import('../pages/admin/survey/AdminSurveyEditPage'))
);
const AdminSurveyPage = Loadable(lazy(() => import('../pages/admin/survey/AdminSurveyPage')));
const AdminReviewsPage = Loadable(lazy(() => import('../pages/admin/review/AdminReviewsPage')));
const AdminReviewsDuePage = Loadable(
  lazy(() => import('../pages/admin/review/AdminReviewsDuePage'))
);
const AdminReviewCreatePage = Loadable(
  lazy(() => import('../pages/admin/review/AdminReviewCreatePage'))
);
const AdminReviewPage = Loadable(lazy(() => import('../pages/admin/review/AdminReviewPage')));
const AdminReviewEditPage = Loadable(
  lazy(() => import('../pages/admin/review/AdminReviewEditPage'))
);

const AdminTimeOffPage = Loadable(lazy(() => import('../pages/admin/AdminTimeOffPage')));

const EmployeeDashboardPage = Loadable(
  lazy(() => import('../pages/employee/EmployeeDashboardPage'))
);
const MyInfo = Loadable(lazy(() => import('../pages/employee/EmployeeMyInfo')));

const EmployeeTrainingTrackerPage = Loadable(
  lazy(() => import('../pages/employee/learning/EmployeeTrainingTrackerPage'))
);
const EmployeeLearningPage = Loadable(
  lazy(() => import('../pages/employee/learning/EmployeeLearningPage'))
);
const EmployeeLearningDetailsPage = Loadable(
  lazy(() => import('../pages/employee/learning/EmployeeLearningDetailsPage'))
);
const EmployeeLearningModulePage = Loadable(
  lazy(() => import('../pages/employee/learning/EmployeeLearningModulePage'))
);
const EmployeeLearningQuizPage = Loadable(
  lazy(() => import('../pages/employee/learning/EmployeeLearningQuizPage'))
);
const EmployeeLearningQuizResultPage = Loadable(
  lazy(() => import('../pages/employee/learning/EmployeeLearningQuizResultPage'))
);

const EmployeeDocumentPage = Loadable(lazy(() => import('../pages/employee/EmployeeDocumentPage')));
const PendingSignatures = Loadable(lazy(() => import('../pages/employee/PendingSignatures')));
const EmployeeProfilePage = Loadable(lazy(() => import('../pages/employee/EmployeeProfilePage')));
const EmployeeTakeSurveyPage = Loadable(
  lazy(() => import('../pages/employee/EmployeeTakeSurveyPage'))
);
const EmployeeReviewPage = Loadable(lazy(() => import('../pages/employee/EmployeeReviewPage')));
const EmployeeCompanyCalendar = Loadable(
  lazy(() => import('../pages/employee/EmployeeCompanyCalendar'))
);

const Organigram = Loadable(lazy(() => import('../sections/shared/orgchart')));

const EmployeeRoutes = [
  {
    path: 'employee',
    element: (
      <RoleBasedGuard accessibleRoles={[ROLE.EMPLOYEE, ROLE.MANAGER, ROLE.ADMIN]}>
        <EmployeeDashboardLayout />
      </RoleBasedGuard>
    ),
    children: [
      { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
      { path: 'dashboard', element: <EmployeeDashboardPage /> },
      // retired legacy document pages (BIZH-1319)
      { path: 'custom-documents', element: <Navigate to="/employee/library" replace /> },
      // learning / courses
      { path: 'learning', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeLearningPage /></FeatureGuard> },
      { path: 'pending-courses', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeePendingCourses /></FeatureGuard> },
      { path: 'completed-courses', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeCompletedCourses /></FeatureGuard> },
      { path: 'my-info', element: <MyInfo /> },
      { path: 'tracker', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeTrainingTrackerPage /></FeatureGuard> },
      { path: 'learning/videos', element: <Navigate to="/employee/library" replace /> },
      { path: 'learning/:id', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeLearningDetailsPage /></FeatureGuard> },
      { path: 'learning/module/:id', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeLearningModulePage /></FeatureGuard> },
      { path: 'learning/module/:id/quiz', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeLearningQuizPage /></FeatureGuard> },
      { path: 'learning/module/:id/quiz/result', element: <FeatureGuard feature={FEATURES.LEARNING}><EmployeeLearningQuizResultPage /></FeatureGuard> },
      // misc
      { path: 'library', element: <EmployeeDocumentPage /> },
      { path: 'hr-resources', element: <Navigate to="/employee/library" replace /> },
      { path: 'pending-signatures', element: <PendingSignatures /> },
      { path: 'my-files', element: <Navigate to="/employee/pending-signatures" replace /> },
      { path: 'time-off', element: <FeatureGuard feature={FEATURES.TIME_OFF}><AdminTimeOffPage /></FeatureGuard> },
      { path: 'profile', element: <EmployeeProfilePage /> },
      { path: 'org-chart', element: <FeatureGuard feature={FEATURES.ORG_CHART}><Organigram /></FeatureGuard> },
      // goals
      { path: 'goals', element: <AdminGoalsPage /> },
      { path: 'goal/:id', element: <AdminGoalPage /> },
      // surveys
      { path: 'surveys', element: <AdminSurveysPage /> },
      { path: 'survey/:id/take', element: <EmployeeTakeSurveyPage /> },
      { path: 'survey/create', element: <AdminSurveyCreatePage /> },
      { path: 'survey/:id', element: <AdminSurveyPage /> },
      { path: 'survey/:id/edit', element: <AdminSurveyEditPage /> },
      // reviews
      { path: 'reviews', element: <AdminReviewsPage /> },
      { path: 'review/upcoming', element: <AdminReviewsDuePage /> },
      { path: 'review/create', element: <AdminReviewCreatePage /> },
      { path: 'review/:id', element: <EmployeeReviewPage /> },
      { path: 'review/:id/reviewee', element: <EmployeeReviewPage /> },
      { path: 'review/:id/edit', element: <AdminReviewEditPage /> },
      //  company events
      { path: 'company-events', element: <EmployeeCompanyCalendar /> },
    ],
  },
];

export default EmployeeRoutes;
